import React, { useEffect, useState } from "react";

import { Form } from "antd";
import { cloneDeep } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { selectCompanyId, selectUserRole } from '../../features/auth/authSlice';
import {
  fetchCategories,
  ICategory,
  selectCategories,
  selectCategoriesDict,
} from "../../features/category/categorySlice";
import { selectEvaluationsDict } from '../../features/evaluation/evaluationsSlice';
import {
  createLesson,
  fetchLessons,
  hideModal,
  ILesson,
  MediaTypes,
  selectDisplayModal,
  VideoTypes,
} from "../../features/lesson/lessonSlice";
import {
  fetchTeachers,
} from "../../features/teacher/teacherSlice";
import { UserRole } from '../../features/users/usersSlice';
import { OTHER_CATEGORY_OPTION } from "../common/CategoryField";
import { AfterActions, DialogPopUp } from "../common/DialogPopUp";
import { MainLayout } from "../common/Layouts/Main";
import { LessonForm } from "./Form";
import { FormFooter } from "../common/FormFooter";
import { Prefixes } from "../prefixes";
import { getTranslations } from "../../features/translations/translationsUtils";

const EMPTY_LESSON = {
  title: "",
  category: "",
  description: "",
  tags: [],
  media: [],
};

const INITIAL_VALUES = {
  title: "",
  category: undefined,
  teacher: undefined,
  picture: "",
  firstName: "",
  middleName: "",
  lastName: "",
  secondLastName: "",
  teacherDescription: "",
};

export const NewLesson: React.FunctionComponent = () => {
  const [fileType, setFileType] = useState<MediaTypes>(MediaTypes.IMAGE);
  const [newLesson, setNewLesson] = useState<ILesson>(EMPTY_LESSON);
  const [currentAction, setCurrentAction] = useState<string>("");
  const [youtubeUrlError, setYoutubeUrlError] = useState('');
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const categoriesDict = useSelector(selectCategoriesDict);
  const categoriesIds = useSelector(selectCategories);
  const evaluationsDict = useSelector(selectEvaluationsDict);
  const userRole = useSelector(selectUserRole);
  const companyId = useSelector(selectCompanyId);
  const isModalVisible = useSelector(selectDisplayModal);
  const history = useHistory();

  useEffect(() => {
    dispatch(fetchLessons());
    dispatch(fetchCategories());
    dispatch(fetchTeachers());
  }, [dispatch]);

  const handleAction = (afterAction: AfterActions) => {
    form
      .validateFields()
      .then(() => {
        if (!newLesson?.category) {
          return;
        }
        if (newLesson.category === OTHER_CATEGORY_OPTION) {
          newLesson.category = {
            name: newLesson.categoryName || "",
          } as ICategory;
        }
        const lessonCopy = cloneDeep(newLesson);
        if (newLesson.media[0].videoType === VideoTypes.YOUTUBE) {
          if (!newLesson.media[0].url) {
            setYoutubeUrlError(getTranslations('GENERAL_REQUIRED'));
            return;
          }
          if (
            !newLesson.media[0].url.startsWith('https://www.youtube.com/watch?v=') &&
            !newLesson.media[0].url.startsWith('https://youtu.be/')
          ) {
            setYoutubeUrlError(getTranslations('GENERAL_URL_INVALID'));
            return;
          }
          if (newLesson.media[0].url.startsWith('https://youtu.be/')) {
            lessonCopy.media[0].url = `https://www.youtube.com/watch?v=${newLesson.media[0].url.replace('https://youtu.be/', '')}`;
          }
        }
        delete lessonCopy.categoryName;
        if (userRole !== UserRole.SuperAdmin) {
          if (lessonCopy._id && lessonCopy.companyId !== companyId) { // don't allow editing lesson that don't belong to the company
            return;
          }
          lessonCopy.companyId = companyId; // force to company in case of creating
        }
        if (!lessonCopy.companyId) {
          lessonCopy.companyId = undefined;
        }
        if (lessonCopy.companyId || !lessonCopy.teacher || lessonCopy.teacher === "") {
          delete lessonCopy.teacher;
        }
        delete lessonCopy.teacherName;
        setCurrentAction(afterAction);
        dispatch(
          createLesson({
            ...lessonCopy,
            media: lessonCopy.media?.filter((item) => item.type === fileType),
          })
        );
        form.setFieldsValue(INITIAL_VALUES);
      })
      .catch((_error) => { });
  };
  let saveButtonIsDisable = !newLesson?.evaluation ?
    false :
    (fileType === MediaTypes.VIDEO && newLesson.quizCuePoints?.length !== evaluationsDict[newLesson.evaluation]?.questions.length);
  return (
    <MainLayout
      title={getTranslations('LESSON_CREATE')}
      onGoBack={() => {
        history.push(`${Prefixes.lesson}`);
      }}
      breadcrumbs={[getTranslations('LESSON_TITLE'), getTranslations('LESSON_CREATE_SHORT')]}
    >
      <DialogPopUp
        name={getTranslations('LESSON_TITLE_SINGULAR')}
        action={getTranslations('LESSON_CREATED')}
        visible={isModalVisible}
        onCancel={() => {
          dispatch(hideModal());
          if (currentAction === AfterActions.BACK) {
            history.push(`${Prefixes.lesson}`);
            return;
          }
          setNewLesson(EMPTY_LESSON);
          form.setFieldsValue(INITIAL_VALUES);
        }}
      />
      <LessonForm
        form={form}
        fileType={fileType}
        newLesson={newLesson}
        categories={categoriesDict}
        categoriesIds={categoriesIds}
        setFileType={setFileType}
        setNewLesson={setNewLesson}
        youtubeUrlError={youtubeUrlError}
      />
      <FormFooter
        disabledSave={saveButtonIsDisable}
        editing={false}
        objectName={getTranslations('LESSON_TITLE_SINGULAR').toLocaleLowerCase()}
        onCancel={() => {
          history.push(`${Prefixes.lesson}`);
        }}
        onSave={() => {
          handleAction(AfterActions.BACK);
        }}
        onSaveAndCreateAnother={() => {
          handleAction(AfterActions.STAY);
        }}
      />
    </MainLayout>
  );
};
